import { createContext, useCallback, useMemo } from "react";

// products
import actionTypes from "../../common/V2/actionTypes";
// constants
import { DATABASE_STATUTES, DEFAULT_PRODUCT_FILTER_VALUES } from "../../common/constants"; // helpers
import { productReducer } from "../../reducers/V2";

import axios from "../../utils/axiosV2";
import {
  fetchProductsForCollections,
  completeVariantsIfNeeded,
  MAX_VARIANTS_PER_PRODUCT,
} from "../../utils/shopifyCollectionProducts";
// hooks
import useCustomReducer from "../../hooks/useCustomReducer";

// product constants
const LIST_PRODUCTS = "/v2/products";
const CSV_UPLOAD_URL = "/v2/products/csv-upload";
const CSV_UPLOADS_URL = "/v2/products/csv-uploads";

// Extract integer ID from Shopify GID (e.g., "gid://shopify/Product/10248990392657" -> "10248990392657")
const extractShopifyId = (gid) => (gid ? gid.split("/").pop() : null);

// Transform a Shopify product (from ResourcePicker or Admin GraphQL) into the
// backend create schema. Handles both flat `variants: [...]` shape (ResourcePicker)
// and GraphQL `variants.nodes: [...]` shape (collection resolver).
const transformShopifyProductToCreate = (product, customerExperiences) => {
  const variantsSource = product.variants?.nodes || product.variants || [];

  return {
    type: "MANUAL",
    shopifyProductId: extractShopifyId(product.id),
    title: product.title,
    handle: product.handle,
    img: product.images?.nodes?.[0]?.originalSrc || product.images?.[0]?.originalSrc || null,
    vendor: product.vendor || null,
    tags: product.tags || [],
    variants: variantsSource.map((variant) => ({
      shopifyProductVariantId: extractShopifyId(variant.id),
      title: variant.title,
      sku: variant.sku,
      price: parseFloat(variant.price) || null,
      shopifyQuantity: variant.inventoryQuantity || 0,
      displayName: variant.displayName,
      inventoryPolicy: variant.inventoryPolicy || "DENY",
      shopifyInventoryItemId: extractShopifyId(variant.inventoryItem?.id) || "",
      shopifyUpdatedAt: variant.updatedAt || new Date().toISOString(),
      shopifyDeletedAt: null,
    })),
    customerExperiences: customerExperiences.map((exp) => ({
      id: exp.id,
      startDate: exp.startDate,
      endDate: exp.endDate,
      preOrderQuantity: exp.preorderQuantity,
    })),
  };
};

// Progress bar is split across three phases. The weights keep the bar moving
// smoothly: fetching collection products from Shopify (usually fast), expanding
// variants for the rare products with > 250 variants, and the dominant phase
// of uploading products one by one to our backend.
const PROGRESS_FETCH_WEIGHT = 40;
const PROGRESS_EXPAND_WEIGHT = 5;
const PROGRESS_UPLOAD_WEIGHT = 55;

// Handle product creation when the user picked one or more Shopify collections.
// Shopify's ResourcePicker only returns collection metadata (not the products
// inside), so we resolve products via the Admin GraphQL API on the client,
// then POST them one-by-one to the backend. Progress is driven by the total
// `productsCount` we already have from the picker.
//
// TODO(collections-v2): when the backend exposes a bulk create endpoint
//   (e.g. POST /v2/products/batch), replace the per-product loop below with
//   batches of ~25-50 to cut wall-clock time significantly on large collections.
const createTimesactProductsFromCollections = async ({
  collections,
  customerExperiences,
  updateOperationProgress,
  fetchProducts,
  completeOperation,
}) => {
  const expectedTotal = Math.max(
    collections.reduce((sum, c) => sum + (c.productsCount || 0), 0),
    1
  );

  // Phase 1: resolve collections -> products via Admin GraphQL (with variants).
  let fetchedSoFar = 0;
  const shopifyProducts = await fetchProductsForCollections(
    collections.map((c) => c.id),
    {
      onProductFetched: () => {
        fetchedSoFar++;
        const ratio = Math.min(fetchedSoFar / expectedTotal, 1);
        updateOperationProgress(ratio * PROGRESS_FETCH_WEIGHT);
      },
    }
  );

  if (shopifyProducts.length === 0) {
    await fetchProducts();
    completeOperation();
    return { success: true, created: 0, errors: [] };
  }

  // Phase 2: expand variants for the rare products with > 250 variants.
  const expandedProducts = [];
  for (let i = 0; i < shopifyProducts.length; i++) {
    // eslint-disable-next-line no-await-in-loop
    expandedProducts.push(await completeVariantsIfNeeded(shopifyProducts[i]));
    const ratio = (i + 1) / shopifyProducts.length;
    updateOperationProgress(PROGRESS_FETCH_WEIGHT + ratio * PROGRESS_EXPAND_WEIGHT);
  }

  // Phase 3: transform + POST to backend, one product at a time.
  const errors = [];
  const totalToUpload = expandedProducts.length;

  for (let i = 0; i < totalToUpload; i++) {
    const product = expandedProducts[i];

    if ((product.variants?.nodes?.length || 0) > MAX_VARIANTS_PER_PRODUCT) {
      errors.push({
        shopifyProductId: product.id,
        message: `Skipped: exceeds ${MAX_VARIANTS_PER_PRODUCT} variants per product limit`,
      });
    } else {
      const payload = transformShopifyProductToCreate(product, customerExperiences);
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(LIST_PRODUCTS, payload);
      } catch (error) {
        console.error(`Error creating product ${payload.shopifyProductId}:`, error);
        errors.push({
          shopifyProductId: payload.shopifyProductId,
          message: error?.response?.data?.message || error?.message || "Unknown error",
        });
      }
    }

    const ratio = (i + 1) / totalToUpload;
    updateOperationProgress(PROGRESS_FETCH_WEIGHT + PROGRESS_EXPAND_WEIGHT + ratio * PROGRESS_UPLOAD_WEIGHT);
  }

  await fetchProducts();
  completeOperation();

  return {
    success: errors.length === 0,
    created: totalToUpload - errors.length,
    errors,
  };
};

const initialState = {
  products: [],
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
  csvUploads: [],
  csvUploadsPagination: {},
  statuses: {
    loadProducts: DATABASE_STATUTES.NOT_LOADED,
    loadCsvUploads: DATABASE_STATUTES.NOT_LOADED,
    uploadCsv: DATABASE_STATUTES.NOT_LOADED,
  },
  loading: false,
  error: null,
  operationState: {
    isProcessing: false,
    progress: 0,
    type: null,
    message: "",
  },
};

const ProductsContext = createContext({
  ...initialState,
  fetchProducts: () => {},
  clearSearch: () => {},
  getProductById: () => Promise.resolve(null),
  uploadCsv: () => Promise.resolve(null),
  fetchCsvUploads: () => Promise.resolve(null),
});

const ProductsProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(productReducer, initialState);
  // Fetch products that are in Timesact.
  const fetchProducts = useCallback(
    async ({ page = 1, limit = 25, search = null, sortBy = null, sortOrder = "ASC", ...otherFilters } = {}) => {
      dispatch({ type: actionTypes.FETCH_PRODUCTS });

      try {
        // Map customerExperience to customerExperienceId for backend
        const { customerExperience, ...restFilters } = otherFilters;
        const params = {
          page,
          limit,
          ...(search && { search }),
          ...(sortBy && { sortBy }),
          ...(sortOrder && { sortOrder }),
          ...(customerExperience && { customerExperienceId: customerExperience }),
          ...restFilters,
        };

        const response = await axios.get(LIST_PRODUCTS, { params });

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.FETCH_PRODUCTS_SUCCESS,
            payload: {
              products: response.data.data.products || [],
              pagination: response.data.pagination || {},
            },
          });
        } else {
          throw new Error(response.data.error || "Failed to fetch products");
        }
      } catch (error) {
        console.error("fetchProducts error:", error);
        dispatch({
          type: actionTypes.FETCH_PRODUCTS_FAILURE,
          payload: error.message,
        });
      }
    },
    [dispatch]
  );

  const getProductById = useCallback(async (productId) => {
    try {
      const response = await axios.get(`${LIST_PRODUCTS}/${productId}`);
      if (response.data && response.data.success) {
        return response.data.data.product;
      }
      throw new Error(response.data?.error || "Failed to get product");
    } catch (error) {
      console.error("getProductById error:", error);
      throw error;
    }
  }, []);

  const clearSearch = useCallback(() => {
    dispatch({
      type: actionTypes.CLEAR_SEARCH,
    });
  }, [dispatch]);

  // Operation management functions
  const startOperation = useCallback(
    (type, message) => {
      dispatch({
        type: actionTypes.START_OPERATION,
        payload: { type, message },
      });
    },
    [dispatch]
  );

  const updateOperationProgress = useCallback(
    (progress) => {
      dispatch({
        type: actionTypes.UPDATE_OPERATION_PROGRESS,
        payload: { progress },
      });
    },
    [dispatch]
  );

  const completeOperation = useCallback(() => {
    dispatch({
      type: actionTypes.COMPLETE_OPERATION,
    });
  }, [dispatch]);

  const createTimesactProducts = useCallback(
    async ({ data, customerExperiences = [] }) => {
      dispatch({ type: actionTypes.FETCH_PRODUCTS }); // Set loading state

      try {
        if (data?.type === "products" && data?.products) {
          const productsToCreate = data.products.map((product) =>
            transformShopifyProductToCreate(product, customerExperiences)
          );

          let completedProducts = 0;
          const totalProducts = productsToCreate.length;

          for (const product of productsToCreate) {
            try {
              await axios.post(LIST_PRODUCTS, product);
            } catch (error) {
              console.error(`Error creating product ${product.shopifyProductId}:`, error);
            }
            completedProducts++;
            updateOperationProgress((completedProducts / totalProducts) * 100);
          }

          await fetchProducts();
          completeOperation();
          return { success: true };
        }

        if (data?.type === "collection" && Array.isArray(data?.collections) && data.collections.length > 0) {
          return await createTimesactProductsFromCollections({
            collections: data.collections,
            customerExperiences,
            updateOperationProgress,
            fetchProducts,
            completeOperation,
          });
        }

        throw new Error(`Unsupported source: ${data?.type}`);
      } catch (error) {
        console.error("createTimesactProducts error:", error);
        dispatch({
          type: actionTypes.FETCH_PRODUCTS_FAILURE,
          payload: error.message,
        });
        throw error;
      }
    },
    [dispatch, fetchProducts, updateOperationProgress, completeOperation]
  );

  const deleteProductVariants = useCallback(
    async (productGroups) => {
      console.log("deleteProductVariants called with:", { productGroups });

      try {
        const productEntries = Object.entries(productGroups);
        let completedProducts = 0;
        let removedVariants = 0;
        const totalProducts = productEntries.length;

        // Process each product and its variants
        for (const [productId, variantIds] of productEntries) {
          try {
            const response = await axios.delete(`${LIST_PRODUCTS}/${productId}`, {
              data: { variantIds },
            });

            // Handle 204 No Content response (successful delete)
            if (response.status === 204) {
              removedVariants += variantIds.length;

              // Remove from local state
              dispatch({
                type: actionTypes.DELETE_PRODUCT_SUCCESS,
                payload: { productId, variantIds },
              });
            }

            completedProducts++;

            // Update progress
            updateOperationProgress((completedProducts / totalProducts) * 100);
          } catch (error) {
            console.error(`Error deleting product ${productId}:`, error);
            completedProducts++;

            // Update progress even on error
            updateOperationProgress((completedProducts / totalProducts) * 100);
          }
        }

        // Refresh the products list
        await fetchProducts();

        // Complete operation
        completeOperation();

        return removedVariants;
      } catch (error) {
        console.error("deleteProductVariants error:", error);
        dispatch({
          type: actionTypes.DELETE_PRODUCT_FAILURE,
          payload: error.message,
        });
        throw error;
      }
    },
    [dispatch, updateOperationProgress, completeOperation, fetchProducts]
  );

  const updateTimesactProducts = useCallback(
    async ({ productGroups, customerExperiences }) => {
      console.log("updateTimesactProducts called with:", { productGroups, customerExperiences });

      dispatch({ type: actionTypes.FETCH_PRODUCTS }); // Set loading state

      try {
        const productEntries = Object.entries(productGroups);
        let completedProducts = 0;
        const totalProducts = productEntries.length;

        // Process each product and its variants
        for (const [productId, variantIds] of productEntries) {
          try {
            const response = await axios.put(`${LIST_PRODUCTS}/${productId}/variants`, {
              variantIds: variantIds,
              customerExperiences: customerExperiences.map((exp) => ({
                id: exp.id,
                startDate: exp.startDate,
                endDate: exp.endDate,
                preOrderQuantity: exp.preorderQuantity,
              })),
            });

            completedProducts++;

            // Update progress
            updateOperationProgress((completedProducts / totalProducts) * 100);
          } catch (error) {
            console.error(`Error updating product ${productId}:`, error);
            completedProducts++;

            // Update progress even on error
            updateOperationProgress((completedProducts / totalProducts) * 100);
          }
        }

        // Refresh the products list
        await fetchProducts();

        // Complete operation
        completeOperation();

        return { success: true };
      } catch (error) {
        console.error("updateTimesactProducts error:", error);
        dispatch({
          type: actionTypes.FETCH_PRODUCTS_FAILURE,
          payload: error.message,
        });
        throw error;
      }
    },
    [dispatch, fetchProducts, updateOperationProgress, completeOperation]
  );

  const changeVariantCustomerExperience = useCallback(
    async (productId, variantId, fromCustomerExperienceId, toCustomerExperienceId) => {
      console.log("changeVariantCustomerExperience called with:", {
        productId,
        variantId,
        fromCustomerExperienceId,
        toCustomerExperienceId,
      });

      try {
        const response = await axios.put(`${LIST_PRODUCTS}/${productId}/variants/${variantId}/customer-experiences`, {
          fromCustomerExperienceId: String(fromCustomerExperienceId),
          toCustomerExperienceId: String(toCustomerExperienceId),
        });

        if (response.data && response.data.success) {
          const { data } = response.data;

          // Update the local state with the new variant customer experience
          console.log("Dispatching UPDATE_VARIANT_CUSTOMER_EXPERIENCE_SUCCESS with:", {
            productId: data.productId,
            variantId: data.variantId,
            variantCustomerExperience: data.variantCustomerExperience,
          });
          dispatch({
            type: actionTypes.UPDATE_VARIANT_CUSTOMER_EXPERIENCE_SUCCESS,
            payload: {
              productId: data.productId,
              variantId: data.variantId,
              variantCustomerExperience: data.variantCustomerExperience,
            },
          });

          return {
            success: true,
            data,
            message: "Customer experience updated successfully",
          };
        } else {
          throw new Error(response.data.error || "Failed to update customer experience");
        }
      } catch (error) {
        console.error("changeVariantCustomerExperience error:", error);

        dispatch({
          type: actionTypes.UPDATE_VARIANT_CUSTOMER_EXPERIENCE_FAILURE,
          payload: error.response?.data?.message || error.message || "Failed to update customer experience",
        });

        throw error;
      }
    },
    [dispatch]
  );

  const uploadCsv = useCallback(
    async (file) => {
      dispatch({ type: actionTypes.UPLOAD_CSV });
      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(CSV_UPLOAD_URL, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data?.success) {
          dispatch({
            type: actionTypes.UPLOAD_CSV_SUCCESS,
            payload: response.data.data.upload,
          });
          return response.data.data.upload;
        }
        throw new Error(response.data?.message || "Failed to upload CSV");
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        const errors = error.response?.data?.errors || [];
        dispatch({ type: actionTypes.UPLOAD_CSV_FAILURE, payload: message });
        throw { message, errors };
      }
    },
    [dispatch]
  );

  const fetchCsvUploads = useCallback(
    async ({ page = 1, limit = 25 } = {}) => {
      dispatch({ type: actionTypes.FETCH_CSV_UPLOADS });
      try {
        const response = await axios.get(CSV_UPLOADS_URL, { params: { page, limit } });

        if (response.data?.success) {
          dispatch({
            type: actionTypes.FETCH_CSV_UPLOADS_SUCCESS,
            payload: {
              uploads: response.data.data.uploads || [],
              pagination: response.data.pagination || {},
            },
          });
          return response.data.data.uploads;
        }
        throw new Error(response.data?.message || "Failed to fetch CSV uploads");
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_CSV_UPLOADS_FAILURE,
          payload: error.message,
        });
      }
    },
    [dispatch]
  );

  const memoizedState = useMemo(
    () => ({
      ...state,
      dispatch,
      fetchProducts,
      clearSearch,
      getProductById,
      createTimesactProducts,
      deleteProductVariants,
      updateTimesactProducts,
      changeVariantCustomerExperience,
      startOperation,
      updateOperationProgress,
      completeOperation,
      uploadCsv,
      fetchCsvUploads,
    }),
    [
      state,
      dispatch,
      fetchProducts,
      clearSearch,
      getProductById,
      createTimesactProducts,
      deleteProductVariants,
      updateTimesactProducts,
      changeVariantCustomerExperience,
      startOperation,
      updateOperationProgress,
      completeOperation,
      uploadCsv,
      fetchCsvUploads,
    ]
  );

  return <ProductsContext.Provider value={memoizedState}>{children}</ProductsContext.Provider>;
};

export { ProductsContext, ProductsProvider };
